/* Sleek cart drawer. Replaces the Symmetry drawer but answers the same events,
   so theme product forms, quick add and apps that open or refresh the drawer keep working. */
if (!customElements.get('sfj-cart')) {
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const money = (c) => '$' + (Number(c) / 100).toFixed(2);
  const esc = (s) => String(s).replace(/[&<>"']/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m]));
  const post = (url, body) => fetch(root + url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });

  class SfjCart extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.recCache = {};
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('submit', (e) => this.onSubmit(e));
      const on = (ev, fn) => document.addEventListener(ev, fn);
      ['dispatch:cart-drawer:refresh', 'on:cart:change', 'cart:refresh'].forEach((ev) => on(ev, () => this.refresh()));
      if (!this.hasAttribute('data-static')) {
        on('dispatch:cart-drawer:open', (e) => this.open(e.detail && e.detail.opener));
        on('theme:open-cart-drawer', () => this.open());
        on('dispatch:cart-drawer:close', () => this.close());
        on('keydown', (e) => { if (e.key === 'Escape' && this.hasAttribute('open')) this.close(); });
      }
      this.initRecs();
      this.mountExpress();
    }

    /* Express pay: Shopify renders every wallet you have on. Keep only the ones listed in the section setting. */
    mountExpress() {
      const box = this.querySelector('[data-sfj-express]');
      const tpl = box && box.querySelector('template');
      if (!tpl) return;
      const allow = (box.dataset.wallets || '').split(',').map((w) => w.trim()).filter(Boolean);
      const frag = tpl.content.cloneNode(true);
      if (allow.length) {
        frag.querySelectorAll('[wallet-configs]').forEach((el) => {
          try { el.setAttribute('wallet-configs', JSON.stringify(JSON.parse(el.getAttribute('wallet-configs')).filter((w) => allow.includes(w.name)))); } catch (e) {}
        });
      }
      tpl.replaceWith(frag);
    }

    get inner() { return this.querySelector('[data-sfj-cart-inner]'); }

    open(opener) {
      this.opener = opener || document.activeElement;
      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('sfj-cart-open');
      setTimeout(() => this.querySelector('.sfj-cart__panel').focus({ preventScroll: true }), 60);
      this.initRecs();
      this.dispatchEvent(new CustomEvent('on:cart-drawer:after-open', { bubbles: true }));
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('sfj-cart-open');
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('on:cart-drawer:after-close', { bubbles: true }));
    }

    /* Re-render from the server. Several events can fire for one add, so calls are merged. */
    refresh() {
      clearTimeout(this.rt);
      if (!this.pending) {
        this.pending = new Promise((resolve) => {
          this.rt = setTimeout(async () => {
            try {
              const id = this.dataset.sectionId;
              const res = await fetch(`${this.dataset.url || root}?sections=${encodeURIComponent(id)}`);
              const data = await res.json();
              const doc = new DOMParser().parseFromString(data[id], 'text/html');
              const fresh = doc.querySelector('[data-sfj-cart-inner]');
              if (fresh) {
                const body = this.querySelector('[data-sfj-cart-body]');
                const scroll = body ? body.scrollTop : 0;
                const openRows = [...this.querySelectorAll('details[open] summary')].map((s) => s.textContent.trim());
                this.inner.innerHTML = fresh.innerHTML;
                this.querySelectorAll('details summary').forEach((s) => { if (openRows.includes(s.textContent.trim())) s.parentElement.open = true; });
                const nb = this.querySelector('[data-sfj-cart-body]');
                if (nb) nb.scrollTop = scroll;
                this.initRecs();
                this.mountExpress();
              }
              document.dispatchEvent(new CustomEvent('on:cart:after-merge', { bubbles: true }));
            } catch (err) { console.error('Cart refresh failed', err); }
            this.pending = null;
            resolve();
          }, 40);
        });
      }
      return this.pending;
    }

    async run(task) {
      this.classList.add('is-busy');
      let error = '';
      try {
        const res = await task();
        if (res && !res.ok) {
          const d = await res.json().catch(() => ({}));
          error = d.description || d.message || 'Something went wrong. Try again.';
        }
      } catch (e) { error = 'Something went wrong. Try again.'; }
      await this.refresh();
      document.querySelectorAll('sfj-cart').forEach((c) => { if (c !== this && c.refresh) c.refresh(); });
      this.classList.remove('is-busy');
      if (error) {
        const box = this.querySelector('[data-sfj-cart-err]');
        if (box) { box.textContent = error; box.hidden = false; }
      }
    }

    onClick(e) {
      if (e.target.closest('[data-sfj-cart-close]')) { e.preventDefault(); this.close(); return; }
      const q = e.target.closest('[data-sfj-qty]');
      if (q) {
        const line = Number(q.closest('[data-line]').dataset.line);
        this.run(() => post('cart/change.js', { line, quantity: Number(q.dataset.sfjQty) }));
        return;
      }
      const add = e.target.closest('[data-sfj-add]');
      if (add) {
        e.preventDefault();
        this.run(() => post('cart/add.js', { items: [{ id: Number(add.dataset.sfjAdd), quantity: 1 }] }))
          .then(() => document.dispatchEvent(new CustomEvent('on:cart:add', { detail: { variantId: Number(add.dataset.sfjAdd) } })));
        return;
      }
      const rm = e.target.closest('[data-sfj-disc-remove]');
      if (rm) {
        const form = this.querySelector('[data-sfj-disc]');
        const codes = (form.dataset.codes || '').split(',').filter((c) => c && c !== rm.dataset.sfjDiscRemove);
        this.run(() => post('cart/update.js', { discount: codes.join(',') }));
      }
    }

    async onSubmit(e) {
      const form = e.target.closest('[data-sfj-disc]');
      if (!form) return;
      e.preventDefault();
      const code = form.discount.value.trim();
      if (!code) return;
      const codes = (form.dataset.codes || '').split(',').filter(Boolean);
      if (!codes.some((c) => c.toLowerCase() === code.toLowerCase())) codes.push(code);
      await this.run(() => post('cart/update.js', { discount: codes.join(',') }));
      const now = this.querySelector('[data-sfj-disc]');
      const ok = now && (now.dataset.codes || '').toLowerCase().split(',').includes(code.toLowerCase());
      if (!ok) {
        const wrap = this.querySelector('[data-sfj-disc-wrap]');
        const msg = this.querySelector('[data-sfj-disc-msg]');
        if (wrap) wrap.open = true;
        if (msg) { msg.textContent = `"${code}" isn't a valid code.`; msg.hidden = false; }
      }
    }

    /* Complete the look: related products for the first item, minus what's already in the cart. */
    async initRecs() {
      const box = this.querySelector('[data-sfj-recs]');
      if (!box || !box.dataset.pid || box.dataset.done) return;
      box.dataset.done = '1';
      const pid = box.dataset.pid;
      const inCart = (box.dataset.ids || '').split(',');
      try {
        if (!this.recCache[pid]) {
          const r = await fetch(`${root}recommendations/products.json?product_id=${pid}&limit=10`);
          this.recCache[pid] = (await r.json()).products || [];
        }
        const list = this.recCache[pid].filter((p) => p.available && !inCart.includes(String(p.id))).slice(0, 6);
        if (!list.length) return;
        box.querySelector('[data-sfj-recs-track]').innerHTML = list.map((p) => {
          const v = p.variants.find((x) => x.available) || p.variants[0];
          const img = p.featured_image ? `<img src="${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=160" alt="" loading="lazy">` : '';
          const name = esc(p.title.split(' - ')[0]);
          const price = (p.compare_at_price > p.price ? `<s>${money(p.compare_at_price)}</s>` : '') + money(p.price);
          const btn = p.variants.length > 1
            ? `<a class="sfj-rec__add" href="${p.url}" aria-label="Choose options for ${name}">+</a>`
            : `<button type="button" class="sfj-rec__add" data-sfj-add="${v.id}" aria-label="Add ${name}">+</button>`;
          return `<div class="sfj-rec"><a class="sfj-rec__img" href="${p.url}" tabindex="-1">${img}</a><a class="sfj-rec__txt" href="${p.url}"><div class="sfj-rec__name">${name}</div><div class="sfj-rec__price">${price}</div></a>${btn}</div>`;
        }).join('');
        box.hidden = false;
      } catch (e) { /* recommendations are optional */ }
    }
  }
  customElements.define('sfj-cart', SfjCart);
}
